// Local cart for a guest: a visitor with no account, hence no ioi sys Subscription to
// hold the selection. Only intents are stored - {product_id, qty}. Never a price, a
// name or a total: those are read from the catalogue each time the cart is shown,
// because prices can change between visits and a stored cart outlives the visit.
// The server applies its own rules again when the cart is merged at login.
//
// flt, cint, get_number_format and get_number_format_info are globals on website
// pages: frappe-web.bundle.js imports frappe/utils/number_format.js, which assigns
// them to window. They are referenced through window to make that explicit.

frappe.provide("ioi_partners")

const STORAGE_KEY = "ioi_partners.guest_cart"
// Bumped if the stored shape ever changes; any other version reads as empty.
const STORAGE_VERSION = 1
// Ids per catalogue request. Must not exceed ProductQuery.page_length (20, in
// eshop/query.py): a batch that size always fits in a single page, so every request
// is sent with start 0 and nothing is paged.
const CATALOGUE_BATCH_SIZE = 20
const CHANGE_EVENT = "ioi_partners:guest_cart_change"

// Catalogue rows already fetched on this page, keyed by product_id. Per page load
// only: a reload always reads current prices. Ids a successful lookup did not return
// (unpublished, deleted) are remembered too, so they are not refetched on every
// change and count() can leave them out.
const catalogue_rows = new Map()
const unavailable_ids = new Set()
// Lookups in flight, keyed by each product_id they fetch. A change that lands while a
// lookup is running waits for it instead of requesting the same ids again - clicking
// + repeatedly is the normal interaction on the catalogue.
const pending_lookups = new Map()

function read() {
	try {
		const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null")
		if (!data || data.v !== STORAGE_VERSION || !Array.isArray(data.items)) {
			return []
		}
		// Rebuilt field by field, so nothing but product_id and qty survives a
		// hand-edited or foreign entry.
		return data.items
			.filter(item => item && typeof item.product_id === "string" && item.product_id
				&& Number.isInteger(item.qty) && item.qty > 0)
			.map(item => ({ product_id: item.product_id, qty: item.qty }))
	} catch (e) {
		// Storage blocked (private mode, disabled site data) or unreadable JSON.
		return []
	}
}

function write(items) {
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify({
			v: STORAGE_VERSION,
			items: items.map(item => ({ product_id: item.product_id, qty: item.qty })),
			// A new id for every new content: see merge_id().
			merge_id: new_merge_id(),
		}))
	} catch (e) {
		return false
	}
	notify()
	return true
}

function notify() {
	window.dispatchEvent(new CustomEvent(CHANGE_EVENT))
}

// Identifies this content of the guest cart to merge_guest_cart() (www/subscription.py),
// which keeps each merge's result under its id: the same cart sent again - from a second
// tab, or retried after a lost response - gets that result back instead of being added
// to the account twice. Every write() stores a new id, so a changed cart is always merged.
function merge_id() {
	try {
		const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null")
		if (!data || data.v !== STORAGE_VERSION) {
			return new_merge_id()
		}
		if (typeof data.merge_id !== "string" || !MERGE_ID_PATTERN.test(data.merge_id)) {
			// Stored without an id, or with one the server would refuse. Given a new one in
			// place, with no change event: the items are the same.
			data.merge_id = new_merge_id()
			localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
		}
		return data.merge_id
	} catch (e) {
		return new_merge_id()
	}
}

// Same pattern as GUEST_CART_MERGE_ID on the server.
const MERGE_ID_PATTERN = /^[A-Za-z0-9]{16,64}$/

function new_merge_id() {
	return frappe.utils.get_random(24)
}

function qty(product_id) {
	const line = read().find(item => item.product_id === product_id)
	return line ? line.qty : 0
}

// Units, not lines, like get_cart_count(): 40 licences over 3 products is 40. An item
// the catalogue no longer returns stays stored but is not counted: the visitor cannot
// see it anywhere on the catalogue page, and the floating cart leaves it out too. Until
// a lookup has answered for an item, it is counted.
function count() {
	return read()
		.filter(item => !unavailable_ids.has(item.product_id))
		.reduce((total, item) => total + item.qty, 0)
}

// A quantity of 0 or less removes the line, as update_cart_line() does.
function set_qty(product_id, new_qty) {
	const items = read()
	const line = items.find(item => item.product_id === product_id)
	new_qty = window.cint(new_qty)

	if (new_qty > 0) {
		if (line) {
			line.qty = new_qty
		} else {
			items.push({ product_id, qty: new_qty })
		}
	} else if (line) {
		items.splice(items.indexOf(line), 1)
	} else {
		return true
	}

	return write(items)
}

// add_cart_line(): a new line at 1, an existing one + 1.
function add(product_id) {
	return set_qty(product_id, qty(product_id) + 1)
}

// remove_product_from_subscription(): one unit less, the line gone at 0.
function remove_one(product_id) {
	return set_qty(product_id, qty(product_id) - 1)
}

function clear() {
	try {
		localStorage.removeItem(STORAGE_KEY)
	} catch (e) {
		return false
	}
	notify()
	return true
}

// The server sends in_cart: false on every catalogue item for a guest, since it
// holds no cart for them. This sets in_cart / total_in_cart from the local cart.
function mark_in_cart(products) {
	const items = read()
	for (const product of products) {
		const line = items.find(item => item.product_id === product.name)
		product.total_in_cart = line ? line.qty : 0
		product.in_cart = product.total_in_cart > 0
	}
}

async function request_batch(batch) {
	const r = await frappe.call({
		method: "ioi_partners.eshop.api.get_product_filter_data",
		args: {
			query_args: { field_filters: { product_id: batch }, start: 0 },
		},
	})
	const message = r && r.message
	if (!message || message.exc) {
		throw new Error("Catalogue lookup failed")
	}

	const returned = new Set()
	for (const item of message.items || []) {
		catalogue_rows.set(item.name, item)
		returned.add(item.name)
	}

	// Asked for and not returned: unpublished or deleted. Only a successful answer is
	// definitive, and only for its own batch. A newly unavailable item changes count(),
	// so the change event goes out and the badge is redrawn without it.
	let newly_unavailable = false
	for (const id of batch) {
		if (!returned.has(id) && !unavailable_ids.has(id)) {
			unavailable_ids.add(id)
			newly_unavailable = true
		}
	}
	if (newly_unavailable) {
		notify()
	}
}

async function fetch_catalogue_rows(product_ids) {
	const missing = product_ids.filter(id => !catalogue_rows.has(id) && !unavailable_ids.has(id))

	// Ids no lookup is fetching yet, in batches. The loop never starts an empty batch:
	// build_fields_filters() skips an empty value, and the lookup would return the
	// whole catalogue.
	const unrequested = missing.filter(id => !pending_lookups.has(id))
	for (let i = 0; i < unrequested.length; i += CATALOGUE_BATCH_SIZE) {
		const batch = unrequested.slice(i, i + CATALOGUE_BATCH_SIZE)
		const lookup = request_batch(batch).finally(() => {
			for (const id of batch) {
				pending_lookups.delete(id)
			}
		})
		for (const id of batch) {
			pending_lookups.set(id, lookup)
		}
	}

	// Every missing id is now covered, by a lookup started above or one already in flight.
	await Promise.all(new Set(missing.map(id => pending_lookups.get(id))))
}

// A cart line in the shape get_subscription() returns subscription_items, so the
// cart components read a guest line like a server one. name is the line id they key
// on; a guest cart has one line per product.
function to_line(item, row, today) {
	return {
		name: item.product_id,
		product_id: item.product_id,
		// The catalogue's resolved title (query.py add_display_details), as
		// with_product_names() gives a server line.
		product_name: row.display_name || row.product_name,
		product_family: row.product_family,
		status: "In Basket",
		// set_start_dates() gives every "In Basket" line these on each save, so a guest
		// line shows the same "Since" group and renewal date as a server one. Formatted
		// as dates: frappe.datetime.add_months() returns a full timestamp.
		start_date: today,
		renewal_date: window.moment(today).add(36, "months").format("YYYY-MM-DD"),
		qty_in_basket: item.qty,
		qty_ordered: 0,
		active_qty: 0,
		enduser_price: row.price,
		currency: row.currency || "EUR",
		install_cost: row.install_cost,
		pricing_unit: row.pricing_unit,
		is_recurring: row.is_recurring,
		estimated_setup_time: row.estimated_setup_time,
		unit_of_measure: row.unit_of_measure,
		allow_quantity: row.allow_quantity,
		best_max_user: row.best_max_user,
		eval: row.available_in_eval,
		// The variable price and its rule as the catalogue resolved them (query.py
		// add_price_rules), as a server line has them (with_product_names).
		variable_price: row.variable_price,
		price_rule: row.price_rule,
		price_rule_lang: row.price_rule_lang,
		// The promotion's badge as the catalogue gave it (query.py add_promotions): the enabled promotion's, when
		// it lists the product, as with_product_names() gives a server line in the basket.
		promotion_badge: row.promotion_badge,
	}
}

// Precision and rounding method as calculate_totals() applies them, handed to the page
// by get_currency_rounding() (eshop/query.py). Frappe's website boot carries neither,
// so flt() would otherwise use the number format's precision and Banker's rounding.
// The fallback, for a page that does not provide them, mirrors get_field_precision()
// for a Currency field with no precision and no currency_precision default.
function currency_rounding() {
	if (frappe.boot.eshop_currency_rounding) {
		return frappe.boot.eshop_currency_rounding
	}
	const precision = window.get_number_format_info(window.get_number_format()).precision
	return {
		subscription_total_in_basket: precision,
		setup_total_in_basket: precision,
		rounding_method: undefined,
	}
}

// ioisysSubscription.calculate_totals(), basket branch: quantity x price per line,
// into the recurring or the setup bucket by the Pricing Unit's is_recurring, plus
// install_cost and estimated_setup_time once per line, not multiplied by quantity.
function basket_totals(lines) {
	let subscription_total = 0.0
	let setup_total = 0.0
	let setup_time = 0.0
	let total = 0.0

	for (const line of lines) {
		const amount = window.flt(line.qty_in_basket) * window.flt(line.enduser_price)
		if (line.is_recurring) {
			subscription_total += amount
		} else {
			setup_total += amount
		}
		setup_total += window.flt(line.install_cost)
		total += amount + window.flt(line.install_cost)
		setup_time += window.flt(line.estimated_setup_time)
	}

	const rounding = currency_rounding()
	return {
		// Rounded to their field precision, as calculate_totals() does at the end...
		subscription_total_in_basket: window.flt(
			subscription_total, rounding.subscription_total_in_basket, null, rounding.rounding_method
		),
		setup_total_in_basket: window.flt(
			setup_total, rounding.setup_total_in_basket, null, rounding.rounding_method
		),
		// ...while these two are accumulated and never rounded there.
		setup_time_in_basket: setup_time,
		total_in_basket: total,
	}
}

// The local cart in the shape of a get_subscription() payload: "In Basket" lines at
// the catalogue's current price, plus basket totals. Rules only the server applies -
// dependency lines, the erp-boost cap, forbidden pairs - are not reproduced here; the
// merge at login reports what they change.
//
// Never rejects. A failed lookup sets failed: true and returns the lines it could
// price; the stored items it could not are listed in unpriced, so a caller never has
// to mistake a failed lookup for an empty cart. unavailable lists the items the
// catalogue answered for and did not return.
async function to_subscription() {
	const items = read()
	let failed = false

	try {
		await fetch_catalogue_rows(items.map(item => item.product_id))
	} catch (e) {
		failed = true
	}

	const lines = []
	const unavailable = []
	const unpriced = []
	// Today in the system timezone, which is what the server's nowdate() uses in
	// set_start_dates(). frappe.datetime.nowdate() reads the user timezone instead: it
	// only matches for a guest because the website boot falls back to the system zone.
	const time_zone = frappe.boot.time_zone?.system || frappe.sys_defaults.time_zone
	const today = window.moment.tz(time_zone).format("YYYY-MM-DD")
	for (const item of items) {
		const row = catalogue_rows.get(item.product_id)
		if (row) {
			lines.push(to_line(item, row, today))
		} else if (unavailable_ids.has(item.product_id)) {
			unavailable.push(item)
		} else {
			unpriced.push(item)
		}
	}

	return Object.assign(
		{
			subscription_items: lines,
			unavailable: unavailable,
			unpriced: unpriced,
			failed: failed,
			currency: lines.length ? lines[0].currency : "EUR",
		},
		basket_totals(lines)
	)
}

ioi_partners.guest_cart = {
	CHANGE_EVENT,
	items: read,
	qty,
	count,
	set_qty,
	add,
	remove_one,
	clear,
	merge_id,
	mark_in_cart,
	to_subscription,
}

// A change made in another tab reaches this one through the storage event.
// key is null when the whole storage was cleared.
window.addEventListener("storage", (event) => {
	if (event.key === STORAGE_KEY || event.key === null) {
		notify()
	}
})
